import { useRef, useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

/* ─── Config ─── */
const LONG_PRESS_MS = 480

/* ─── Toast ─── */
let toastTimer: ReturnType<typeof setTimeout> | null = null

function showToast(msg: string) {
  const el = document.getElementById('app-toast')
  if (!el) return
  el.textContent = msg
  el.classList.add('show')
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => el.classList.remove('show'), 2200)
}

export function AppToast() {
  return <div id="app-toast" className="app-toast" role="status" />
}

/* ─── Voice Overlay ─── */
function VoiceOverlay({ visible }: { visible: boolean }) {
  if (!visible) return null
  return createPortal(
    <div className="voice-overlay">
      <div className="voice-panel">
        <div className="voice-ring">
          <Icon name="mic" size={32} className="text-primary" />
        </div>
        <p className="text-base font-semibold text-[var(--color-text-primary)]">
          正在聆听…
        </p>
        <p className="text-sm mt-1.5" style={{ color: 'var(--color-text-secondary)' }}>
          松开发送
        </p>
      </div>
    </div>,
    document.body,
  )
}

/* ─── Tab Config ─── */
interface TabItem {
  path: string
  icon: string
  label: string
}

const TABS: TabItem[] = [
  { path: '/', icon: 'dynamic_feed', label: 'nav.feed' },
  { path: '/conversations', icon: 'forum', label: 'nav.interactions' },
  { path: '/my-agent', icon: 'smart_toy', label: 'nav.myAgent' },
  { path: '/tasks', icon: 'task_alt', label: 'nav.tasks' },
  { path: '/profile', icon: 'badge', label: 'nav.portfolio' },
]

/* ─── Main TabBar ─── */
export function TabBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [voiceActive, setVoiceActive] = useState(false)
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressClickRef = useRef(false)
  const agentBtnRef = useRef<HTMLButtonElement>(null)

  const isActive = useCallback(
    (path: string) =>
      path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(path),
    [location.pathname],
  )

  /* ─── Long press handlers (my-agent tab only) ─── */
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    try { agentBtnRef.current?.setPointerCapture(e.pointerId) } catch {}
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null
      setVoiceActive(true)
      if (navigator.vibrate) navigator.vibrate(12)
    }, LONG_PRESS_MS)
  }, [])

  const onPointerEnd = useCallback(
    (e: React.PointerEvent) => {
      if (longPressRef.current) {
        clearTimeout(longPressRef.current)
        longPressRef.current = null
      }
      if (voiceActive) {
        setVoiceActive(false)
        showToast('语音已发送 (demo)')
        suppressClickRef.current = true
        e.preventDefault()
      }
      try { agentBtnRef.current?.releasePointerCapture(e.pointerId) } catch {}
    },
    [voiceActive],
  )

  const onAgentClick = useCallback(() => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    navigate('/my-agent')
  }, [navigate])

  useEffect(() => {
    return () => {
      if (longPressRef.current) clearTimeout(longPressRef.current)
    }
  }, [])

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-black/[0.06] dark:border-white/[0.08] md:hidden"
        style={{
          backgroundColor: 'var(--tabbar-bg, #FFFFFF)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        <div className="px-2 py-2">
          <div className="flex justify-around items-center">
            {TABS.map((tab) => {
              const active = isActive(tab.path)
              const isCenter = tab.path === '/my-agent'

              // My Agent tab: enlarged icon in themed rounded-rect button, long-press for voice
              if (isCenter) {
                return (
                  <button
                    key={tab.path}
                    ref={agentBtnRef}
                    onClick={onAgentClick}
                    onPointerDown={onPointerDown}
                    onPointerUp={onPointerEnd}
                    onPointerCancel={onPointerEnd}
                    className={`
                      flex items-center justify-center w-16 h-9 rounded-lg
                      bg-primary shadow-[0_2px_12px_rgba(0,217,160,0.3)]
                      transition-transform
                      ${voiceActive ? 'scale-[1.08] shadow-[0_0_0_4px_rgba(0,217,160,0.15),0_4px_16px_rgba(0,217,160,0.4)]' : ''}
                    `}
                    style={{
                      touchAction: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                    aria-label="Agent"
                  >
                    <Icon
                      name={voiceActive ? 'mic' : tab.icon}
                      size={28}
                      filled
                      className={`text-white ${voiceActive ? 'animate-pulse' : ''}`}
                    />
                  </button>
                )
              }

              // Normal tabs
              return (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  className={`flex items-center flex-col gap-0.5 justify-center min-w-[48px] ${
                    active ? 'text-primary' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <Icon
                    name={tab.icon}
                    size={24}
                    filled={active}
                  />
                  <span className={`text-[10px] ${active ? 'font-semibold' : 'font-medium'}`}>
                    {t(tab.label)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </nav>

      <VoiceOverlay visible={voiceActive} />
    </>
  )
}
